import { HttpClient } from "@angular/common/http";
import { Inject, Injectable, Injector, signal, WritableSignal } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { TenantService } from "../tenant/tenant.service";
import { firstValueFrom, Observable, Subscription, timer } from "rxjs";
import { StaticDataResponse } from "src/types/static-data-response";
import { Environment } from "src/types/environment";
import { ENV } from "src/providers/environment.provider";
import { LedgerCodeResponseDto } from "src/types/ledger-code";
import { DivisionResponseDto } from "src/types/division";
import { CostCentreResponseDto } from "src/types/cost-centre";
import { Country } from "src/types/country";
import { Currency } from "src/types/currency";
import { SalesProductResponseDto } from "src/types/sales-product";
import { FixedAssetsBookResponseDto } from "src/types/modules/fixed-assets/fixed-assets-book";
import { FixedAssetsClass } from "src/types/modules/fixed-assets/fixed-assets-class";
import { GeneralStatus } from "src/types/general-status";
import { PaymentTermData } from "src/types/payment-term";
import { StandardDay } from "src/types/standard-day";
import { InvoiceDisputeReasonResponseDto } from "src/types/invoice-dispute-reason";
import { EconomicIndexResponseDto } from "src/types/modules/configuration/economic-indices/economic-index";
import { VatCodeResponseDto } from "src/types/vat-code";
import { ProjectTypeResponseDto } from "src/types/project-type";
import { ProjectCodeResponseDto } from "src/types/project-code";

export interface RefreshOptions {
  /**
   * Whether the service should retrieve results from it's cache or if it should
   * be forced to update via a HTTP request.
   */
  force?: boolean;
}

const caches = {
  costCentres: signal<CostCentreResponseDto[]>([]), // Done
  countries: signal<Country[]>([]),
  currencies: signal<Currency[]>([]),
  divisions: signal<DivisionResponseDto[]>([]),
  economicIndices: signal<EconomicIndexResponseDto[]>([]),
  fixedAssetBooks: signal<FixedAssetsBookResponseDto[]>([]),
  fixedAssetClasses: signal<FixedAssetsClass[]>([]),
  generalStatuses: signal<GeneralStatus[]>([]),
  invoiceDisputeReasons: signal<InvoiceDisputeReasonResponseDto[]>([]),
  ledgerCodes: signal<LedgerCodeResponseDto[]>([]),
  paymentTerms: signal<PaymentTermData[]>([]),
  projectCodes: signal<ProjectCodeResponseDto[]>([]),
  projectTypes: signal<ProjectTypeResponseDto[]>([]),
  salesProducts: signal<SalesProductResponseDto[]>([]),
  standardDays: signal<StandardDay[]>([]),
  vatCodes: signal<VatCodeResponseDto[]>([]),
} as const;

type CacheType = keyof typeof caches;

type CacheValue<TType extends CacheType> = typeof caches[TType] extends WritableSignal<(infer U)[]> ? U : never

@Injectable({
  providedIn: 'root'
})
export class StaticDataService {
  private timerSub?: Subscription;

  constructor(
    @Inject(ENV) private readonly environment: Environment,
    private readonly tenantService: TenantService,
    private readonly http: HttpClient,
    private readonly injector: Injector
  ) { }

  public async refreshStaticData(): Promise<void> {
    const apiUrl = await this.tenantService.getApiUrl();
    const url = apiUrl + 'static-data';
    const headers = await this.tenantService.getAuthorizationHeaders();
    const response = await firstValueFrom(this.http.get<StaticDataResponse>(url, { headers: headers }));

    caches.costCentres.set(response.costCentres);
    caches.countries.set(response.countries);
    caches.currencies.set(response.currencies);
    caches.divisions.set(response.divisions);
    caches.economicIndices.set(response.economicIndices);
    caches.fixedAssetBooks.set(response.fixedAssetBooks);
    caches.fixedAssetClasses.set(response.fixedAssetClasses);
    caches.generalStatuses.set(response.generalStatuses);
    caches.invoiceDisputeReasons.set(response.invoiceDisputeReasons);
    caches.ledgerCodes.set(response.ledgerCodes);
    caches.paymentTerms.set(response.paymentTerms.map(term => {
      const { paymentTermTypeId, ...paymentTerm } = term;
      return {
        ...paymentTerm,
        paymentTermId: paymentTermTypeId
      };
    }));
    caches.projectCodes.set(response.projectCodes);
    caches.projectTypes.set(response.projectTypes);
    caches.salesProducts.set(response.salesProducts);
    caches.standardDays.set(response.standardDays);
    caches.vatCodes.set(response.vatCodes);

    this.timerSub?.unsubscribe();
    this.timerSub ??= timer(this.environment.staticDataExpireDurationInMS).subscribe(() => {
      console.log('StaticData has expired. Refreshing data...');
      this.refreshStaticData(); // Don't await
    });
  }

  public getAllFromCache<T extends CacheType>(key: T): CacheValue<T>[] {
    return (caches[key] as WritableSignal<CacheValue<T>[]>)();
  }

  public getObservableFromCache<T extends CacheType>(key: T): Observable<CacheValue<T>[]> {
    const cache = caches[key] as WritableSignal<CacheValue<T>[]>;
    return toObservable(cache, { injector: this.injector });
  }

  public async getFromCache<T extends CacheType, TKey extends keyof CacheValue<T>>(
    key: T,
    idProp: TKey,
    id: CacheValue<T>[TKey],
    /**
     * If the cache cannot find the desired item, it will use this function to try and retrieve it as a fallback
     */
    fallback?: () => Promise<CacheValue<T>>
  ): Promise<CacheValue<T>> {
    const cache = caches[key] as WritableSignal<CacheValue<T>[]>;
    const values = cache();
    const value = values.find(x => x[idProp] === id);
    if (value) { return value; }
    if (!fallback) { throw new Error(`Could not find ${key} with id ${id} and no fallback was provided`); }

    const fallbackResult = await fallback();

    // If found add missing value to cache and update.
    cache.update(values => [...values, fallbackResult]);

    return fallbackResult;
  }

  public setCache<T extends CacheType>(key: T, values: CacheValue<T>[]): void {
    const cache = caches[key] as WritableSignal<CacheValue<T>[]>;
    cache.set(values);
  }

  public updateCache<T extends CacheType, TKey extends keyof CacheValue<T>>(key: T, idProp: TKey, value: CacheValue<T>): void {
    const cache = caches[key] as WritableSignal<CacheValue<T>[]>;
    cache.update((values) => {
      const index = values.findIndex(x => x[idProp] === value[idProp])
      if (index < 0) { return [...values, value]; }

      return values.map((existing, existingIndex) => existingIndex === index ? value : existing);
    });
  }

  public removeFromCache<T extends CacheType, TKey extends keyof CacheValue<T>>(
    key: T,
    idProp: TKey,
    id: CacheValue<T>[TKey]): void {
    const cache = caches[key] as WritableSignal<CacheValue<T>[]>;
    cache.update(values => (values.filter(x => x[idProp] !== id)));
  }

  /**
   * A method to dispose of the perpetual timer that triggers static data refreshing.
   * Use at the end of test methods to ensure the timer isn't triggered when the test run attempts to clean up
   */
  public dispose(): void {
    this.timerSub?.unsubscribe();
  }
}
